﻿import { AsyncUtils } from '../main/ObjectUtils.js';

/**
 * Uses a set of event handlers plus a polling mechanism to monitor status changes. Runs from the
 * user calls start() until either the duration is reached or the user calls stop(). The isActive
 * property can be checked to see if monitor is currently active. Once stopped, it can restarted
 * by calling start() again. Also see the Demo class further below for how to use the monitor.
 */
export class PollingStatusMonitor {
  constructor(duration, interval, onGetStatusAsync, onStatusChanged, onIsEqualStatus, onError) {
    this.duration = duration; // Duration in milliseconds
    this.interval = interval; // Inverval in milliseconds

    if (!(onGetStatusAsync && onStatusChanged && onIsEqualStatus && onError)) {
      throw new Error('All event handler parameters must be assigned.');
    }

    // Event handlers:
    this.onStatusChanged = onStatusChanged; // Callback function to handle the status changes
    this.onGetStatusAsync = onGetStatusAsync;
    this.onIsEqualStatus = onIsEqualStatus;
    this.onError = onError;

    // Properties:
    this.priorStatus = null;
    this.status = null;
    this.userData = null;
    this.isActive = false;
    this.startTime = null;
  }

  ///**
  // * This is kind of a private method, but we allow external parties to use it for statuses which
  // * they want to send through the event chain, and are not coming from the polling itself.
  // * @param {any} status
  // */
  //pokeStatus(status) {
  //  this.priorStatus = this.status;
  //  this.status = status;
  //  if (!this.onIsEqualStatus(this)) {
  //    this.onStatusChanged(this);
  //  }
  //}

  /**
   * Internal function to handle the polling logic
   * @returns Nothing (or a Promise, actually).
   */
  async pollAsync() {
    const elapsedTime = Date.now() - this.startTime;
    if (elapsedTime >= this.duration) {
      this.stop(); // Stop polling if the duration has expired
      return;
    }

    try {
      this.priorStatus = this.status;
      this.status = await this.onGetStatusAsync(this);
      if (!this.onIsEqualStatus(this)) {
        this.onStatusChanged(this);
      }
    } catch (error) {
      this.onError(this, error);
    }
    if (this.isActive) { // Ensure we're still active (user could've stopped the monitor in one of the event handlers)
      // Schedule the next poll:
      this.timeoutId = setTimeout(async () => await this.pollAsync(), this.interval);
    }
  }

  /**
   * Starts monitoring (polling).
   * @param {any} userData Optional user data which can be accessed in the event handlers.
   */
  start(userData) {
    if (this.isActive) {
      throw new Error('Monitoring (polling) has already started.');
    }
    this.priorStatus = null;
    this.status = null;
    this.userData = userData;
    this.isActive = true;
    this.startTime = Date.now();
    this.pollAsync();
  }

  /** Stops the monitoring (polling) */
  stop() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
    this.isActive = false;
  }
}

class Demo {
  demo() {
    const handleGetStatusAsync = async monitor => {
      // This is a placeholder. Implement your API call here. Simulated API response with a delay:
      return new Promise(resolve => {
        setTimeout(() => resolve({ id: 'OK', timestamp: new Date() }), 100);
      });
    };

    const handleStatusChanged = monitor => {
      const oldStatus = monitor.priorStatus;
      const newStatus = monitor.status;
      console.log(`Received status change, old status: ${oldStatus}, new status: ${newStatus}, user data: ${monitor.userData}`);
    };

    const handleIsEqualStatus = monitor => {
      if (!monitor.priorStatus && !monitor.status) {
        return true;
      }
      return monitor.status === monitor.priorStatus;
    }

    const handleError = (monitor, error) => {
      console.log(`Received error while polling: ${error.message}`);
    }

    // Monitor for 10 seconds with 1 second interval between each poll:
    this.monitor = new PollingStatusMonitor(10000, 1000, handleGetStatusAsync, handleStatusChanged,
      handleIsEqualStatus, handleError);
    this.monitor.start('Demo');
  }
}

const MonitorResult = Object.freeze({
  Success: 'Success',
  TimedOut: 'TimedOut',
  Aborted: 'Aborted'
});

/**
 * This monitor is still work-in-progress, but if finished, it will stop as soon as it gets a status.
 * The flaw (must be reviewed) is that it uses recursive calling, but maybe it's okay when used in
 * combination with async/await.
 */
class OneTimePollingStatusMonitor {
  constructor() {
    this.shortTimeoutInMs = 1000; // 1 second
    //this.fullTimeoutInMs = 5 * 1000; // 5 seconds
    this.fullTimeoutInMs = 10 * 60 * 1000; // 10 minutes
  }

  abort() {
    this.isAborted = true;
  }

  //** Delegates how to get the status to the caller via the onGetStatusAsync event handler. */
  async getStatusAsync() {
    if (!this.onGetStatusAsync) {
      throw new Error('The onGetStatusAsync async event handler must be assigned.');
    }
    return await this.onGetStatusAsync(this, this.userData);
  }

  async pollStatusAsync() {
    let status = await this.getStatusAsync();
    if (status.result === MonitorResult.Success) {
      // Good!
    } else if (this.isAborted) {
      // The monitor was aborted while we were awaiting (getting) the status...
      status.result = MonitorResult.Aborted;
    } else if (new Date() - this.startTime > this.fullTimeoutInMs) {
      status.result = MonitorResult.TimedOut;
    } else {
      await AsyncUtils.waitAsync(this.shortTimeoutInMs);
      if (this.isAborted) {
        // The monitor was aborted while we were waiting for next poll...
        status.result = MonitorResult.Aborted;
      } else {
        status = await this.pollStatusAsync(); // Recursive call to this method
      }
    }
    return status;
  }

  async waitForStatus(userData) {
    if (this.isWaiting) {
      throw new Error('This monitor does not allow re-entrant calls to waitForStatus()');
    }
    this.isWaiting = true;
    this.startTime = new Date();
    this.userData = userData;
    try {
      await AsyncUtils.waitAsync(this.shortTimeout);
      return await this.pollStatusAsync();
    } finally {
      this.isWaiting = false;
    }
  }
}

